import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Droplets, Wind, BatteryCharging } from 'lucide-react';
export function ResearchFocus() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  return <section id="research" className="py-24 bg-white" data-id="element-262">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-263">
        <div className="text-center max-w-4xl mx-auto mb-16" data-id="element-264">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-3xl md:text-4xl font-bold text-[#800000] font-serif mb-6" data-id="element-265">
            Research Focus
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-lg text-gray-600 leading-relaxed" data-id="element-266">
            The Multiscale Thermal Transport Research Laboratory investigates
            fluid flow and heat transfer across multiple length scales,
            combining experiments, computational modelling, and data-driven
            methods to develop novel cooling solutions for high-power-density
            applications. Our work encompasses both active and passive
            phase-change cooling techniques.
          </motion.p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-267">
          {/* Active Cooling */}
          <motion.div variants={itemVariants} className="bg-[#FAF9F7] rounded-xl p-8 shadow-sm border-t-4 border-[#800000] hover:shadow-md transition-shadow" data-id="element-268">
            <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-sm mb-6 text-[#800000]" data-id="element-269">
              <Droplets size={28} strokeWidth={1.5} data-id="element-270" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4 font-serif" data-id="element-271">
              Active Cooling
            </h3>
            <p className="text-gray-600 leading-relaxed text-sm" data-id="element-272">
              On the active side, we study spray cooling and flow boiling in
              microchannels, with emphasis on understanding the underlying
              physics — droplet impact, coalescence, bubble dynamics, and
              thin-film evaporation — through infrared thermography and
              high-speed imaging.
            </p>
          </motion.div>

          {/* Passive Cooling System */}
          <motion.div variants={itemVariants} className="bg-[#FAF9F7] rounded-xl p-8 shadow-sm border-t-4 border-[#800000] hover:shadow-md transition-shadow" data-id="element-273">
            <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-sm mb-6 text-[#800000]" data-id="element-274">
              <Wind size={28} strokeWidth={1.5} data-id="element-275" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4 font-serif" data-id="element-276">
              Passive Cooling System
            </h3>
            <p className="text-gray-600 leading-relaxed text-sm" data-id="element-277">
              On the passive side, we develop and characterize heat pipes,
              pulsating heat pipes, vapour chambers, and flat thermosyphons,
              with performance enhancement achieved through wettability
              modifications and novel wick architectures. Single-phase cooling
              techniques based on wall jets and impinging jets, relevant to
              combustor liner cooling, are also being explored.
            </p>
          </motion.div>

          {/* Battery Thermal Management */}
          <motion.div variants={itemVariants} className="bg-[#FAF9F7] rounded-xl p-8 shadow-sm border-t-4 border-[#800000] hover:shadow-md transition-shadow" data-id="element-278">
            <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-sm mb-6 text-[#800000]" data-id="element-279">
              <BatteryCharging size={28} strokeWidth={1.5} data-id="element-280" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4 font-serif" data-id="element-281">
              Battery Thermal Management
            </h3>
            <p className="text-gray-600 leading-relaxed text-sm" data-id="element-282">
              These cooling strategies are further applied to battery thermal
              management for electric vehicles, where we investigate both
              liquid-cooled cold plate designs and direct immersion cooling
              approaches for lithium-ion cells.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}